import { useAlgoliaSearch } from "@dxp-feed/algolia";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import type { MiddlewareOptions } from "instantsearch.js/es/types/middleware.d.ts";
import { UiState } from "instantsearch.js";
import { createInsightsMiddleware } from "instantsearch.js/es/middlewares";
import { getGuestUserRandomId } from "@/helpers/global";
import { searchInteractionEventData } from "@/helpers/dataLayer/searchResults";
import { analyticsViewFilterEvent } from "@/services/algolia/analytics";
import { GLOBAL_SEARCH_RESULTS_SECTIONS } from "@/utils/constant";
import { useAppDispatch } from "@/utils/hooks/reactUseCustomHooks";
import { setSearchQuery } from "@/utils/redux/algolia";

export enum MiddlewareCallbackType {
  STATE = "STATE",
  SUBSCRIBE = "SUBSCRIBE",
  UNSUBSCRIBE = "UNSUBSCRIBE",
}

export interface AlgoliaMiddlewareProps {
  pageType: "LISTING" | "SEARCH_RESULT";
  indexes: Array<Record<"label" | "name", string>>;
  prevQuery?: React.MutableRefObject<string>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  userId?: string;
  currentTab?: GLOBAL_SEARCH_RESULTS_SECTIONS;
}
type InitialValuesType = {
  [key in GLOBAL_SEARCH_RESULTS_SECTIONS]?: object;
};

const AlgoliaMiddleware = React.memo(function ({
  userId,
  pageType,
  indexes,
  prevQuery,
  setLoading,
  currentTab,
}: AlgoliaMiddlewareProps) {
  const { addMiddlewares } = useAlgoliaSearch();
  const hasChanged = useRef<boolean>(false);
  const filterView = useRef<InitialValuesType>({});
  const dispatch = useAppDispatch();

  useEffect(() => {
    let key = currentTab;
    if (pageType == "LISTING") {
      key = indexes[0].label.toLowerCase() as GLOBAL_SEARCH_RESULTS_SECTIONS;
    }
    const filter = filterView.current[key!];

    if (filter && Object.keys(filter).length > 0) {
      //@ts-expect-error @ts-ignore
      analyticsViewFilterEvent(filter);
    }
  }, [hasChanged.current, currentTab]);

  function middleware(option: MiddlewareOptions) {
    return {
      onStateChange(_: { uiState: UiState }) {
        try {
          if (!indexes?.length) {
            return;
          }
          if (setLoading) setLoading(true);
          const uiState = option.instantSearchInstance;

          // eslint-disable-next-line no-inner-declarations
          function waitUntilIdle(callback: () => void) {
            if (uiState.status === "idle") {
              callback();
            } else {
              setTimeout(() => waitUntilIdle(callback), 500);
            }
          }

          waitUntilIdle(() => {
            try {
              const indexState = uiState?.renderState;
              let totalCount = 0;
              let query: string | undefined = "";
              const queryIds: Record<
                GLOBAL_SEARCH_RESULTS_SECTIONS,
                string | null
              > = {
                products: "",
                recipes: "",
                articles: "",
              };

              for (const index of indexes!) {
                const indexDetail = indexState[index.name];
                if (indexDetail) {
                  const count = indexDetail?.stats?.nbHits as number;
                  totalCount += count;
                  query = indexDetail?.stats?.query;

                  queryIds[
                    index.label.toLowerCase() as GLOBAL_SEARCH_RESULTS_SECTIONS
                  ] = indexDetail?.hits?.items?.[0]?.__queryID || null;
                  const currentFilter =
                    indexDetail.hits?.results?._state
                      ?.disjunctiveFacetsRefinements;

                  const filters = currentFilter
                    ? Object.entries(currentFilter).flatMap(([key, value]) => {
                        let arr: string[] = [];
                        if (Array.isArray(value) && value.length) {
                          arr = getFormattedFilterList(key, value);
                        }
                        return arr;
                      })
                    : [];

                  // algolia view filter analytics event.
                  if (filters.length) {
                    filterView.current[
                      index.label.toLowerCase() as GLOBAL_SEARCH_RESULTS_SECTIONS
                    ] = {
                      kind: index.label.toLowerCase(),
                      index: index.name,
                      filters,
                      userToken: userId,
                    };
                  }
                }
              }
              hasChanged.current = !hasChanged.current;
              dispatch(setSearchQuery({ queryID: queryIds }));
              if (pageType == "SEARCH_RESULT") {
                if (prevQuery && prevQuery?.current?.trim() !== query?.trim()) {
                  // @ts-expect-error ignore global variable error
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  dataLayer.push(
                    searchInteractionEventData({
                      searchQuery: query!,
                      searchResultCount: totalCount,
                    })
                  );
                  prevQuery.current = query ?? "";
                }
              }
            } catch (error) {
              console.error(error);
            } finally {
              if (setLoading) setLoading(false);
              // window.scrollTo({ top: 0, behavior: "smooth" });
            }
          });
        } catch (error) {
          console.error(error);
        }
      },
    };
  }

  const analyticsMiddleware = createInsightsMiddleware({
    insightsInitParams: {
      userToken: userId ?? getGuestUserRandomId(),
      authenticatedUserToken: userId,
    },
    // @ts-expect-error @ts-ignore
    insightsClient: window.aa,
    onEvent() {},
  });

  useLayoutEffect(() => {
    return addMiddlewares(middleware, analyticsMiddleware);
  }, [addMiddlewares, userId]);
  return null;
});

export function getFormattedFilterList(key: string, val: string[]): string[] {
  return val.map((item) => `${key}:${encodeURIComponent(item)}`);
}

export default AlgoliaMiddleware;
